import { useCallback, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  PageResponseUserInfo,
  RoleRepresentation,
  UserInfo,
  UsersRequest,
} from '@/api/data-contracts';
import usePermissionsService from '@/features/users/api/permissions.services';
import useUsersService from '@/features/users/api/users.service';
import { queryKeys } from '@/react-query/constants';
import type { FeatureQueryOptions } from '@/shared/api';
import { checkPermission } from '@/shared/lib/permissions';

import { usersApi } from './usersApi';

const { keys } = usersApi;
type ExtraQueryOptions<TData> = Omit<FeatureQueryOptions<TData>, 'queryKey' | 'queryFn'>;

export const useTeamMembersListQuery = (
  request: UsersRequest,
  enabled = true,
  options?: ExtraQueryOptions<PageResponseUserInfo>,
) => {
  const { getAllUsers } = useUsersService();
  return usersApi.useFeatureQuery({
    queryKey: keys.list(request),
    queryFn: (signal) => getAllUsers({ request }, signal),
    enabled,
    ...options,
  });
};

export const useUserDetailsQuery = (
  userId: string,
  enabled = true,
  options?: ExtraQueryOptions<UserInfo>,
) => {
  const { getUserDetails } = useUsersService();
  return usersApi.useFeatureQuery({
    queryKey: [queryKeys.user, userId],
    queryFn: (signal) => getUserDetails(userId, signal),
    enabled: enabled && !!userId,
    ...options,
  });
};

export const useLevelsQuery = (
  enabled = true,
  options?: ExtraQueryOptions<Record<string, RoleRepresentation[]>>,
) => {
  const { getLevels } = usePermissionsService();
  return usersApi.useFeatureQuery({
    queryKey: [queryKeys.levels],
    queryFn: (signal) => getLevels(signal),
    enabled,
    ...options,
  });
};

export const usePermissionsQuery = (
  enabled = true,
  options?: ExtraQueryOptions<RoleRepresentation[]>,
) => {
  const { getPermissions } = usePermissionsService();
  return usersApi.useFeatureQuery({
    queryKey: [queryKeys.permissions],
    queryFn: (signal) => getPermissions(signal),
    enabled,
    ...options,
  });
};

export const useCurrentUserQuery = () => {
  const { getCurrentUserDetails } = useUsersService();
  return useQuery<UserInfo>([queryKeys.currentUser], ({ signal }) => getCurrentUserDetails(signal));
};

export const useHasPermission = (permission: string) => {
  const { data: currentUser } = useCurrentUserQuery();
  return useMemo(() => checkPermission(currentUser, permission), [currentUser, permission]);
};

export const useHasPermissionToCreatePastBookings = () => useHasPermission('create:past-bookings');
export const useIsGuestUser = () => {
  const { data: currentUser } = useCurrentUserQuery();
  return !!currentUser?.isGuest;
};

export const useTeamMembersPrefetchNextPage = () => {
  const queryClient = useQueryClient();
  const { getAllUsers } = useUsersService();
  return useCallback(
    (request: UsersRequest) =>
      queryClient.prefetchQuery({
        queryKey: keys.list(request),
        queryFn: ({ signal }) => getAllUsers({ request }, signal),
      }),
    [queryClient, getAllUsers],
  );
};
